import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parentAssessmentService, schoolService } from '../services/supabase';
import { useAutoSave, useLoadSavedData } from '../hooks';
import SchoolSelector from '../components/SchoolSelector';

// Rating options for parent assessment (0, 1, 2 scale)
const ratingOptions = [
  { value: 0, label: 'Tidak', description: 'Tidak dilakukan', color: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200' },
  { value: 1, label: 'Sebagian', description: 'Sebagian dilakukan', color: 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200' },
  { value: 2, label: 'Ya', description: 'Ya, dilakukan', color: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200' }
];

// Dialog component for alerts
const Dialog = ({ isOpen, onClose, title, children, type = 'info', onConfirm }) => {
  if (!isOpen) return null;

  const typeStyles = {
    success: 'border-green-500 bg-green-50',
    error: 'border-red-500 bg-red-50',
    info: 'border-blue-500 bg-blue-50'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 border-t-4 ${typeStyles[type]}`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <div className="text-gray-600">{children}</div>
          {onConfirm ? (
            <div className="mt-4 flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Lanjutkan
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Tutup
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ParentAssessment = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  // State
  const [questions, setQuestions] = useState({});
  const [rawQuestions, setRawQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [currentComponent, setCurrentComponent] = useState(0);
  const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // School selection state
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showSchoolSelector, setShowSchoolSelector] = useState(true);

  // Storage key for auto-save (scoped to school to prevent cross-school data leakage)
  const storageKey = `parent_assessment_${profile?.id || 'anonymous'}_${selectedSchoolId || 'unselected'}`;

  // Load saved data from localStorage
  const { savedData, clearSavedData } = useLoadSavedData(storageKey);

  // Auto-save to localStorage
  const { isSaving, lastSaved } = useAutoSave(storageKey, { responses, currentComponent, sessionId, selectedSchoolId }, 500);

  // Component order
  const componentOrder = ['Komunikasi dengan orang tua', 'Pengelolaan Sekolah', 'Keterpercayaan'];

  // Load questions when school is selected
  useEffect(() => {
    if (selectedSchoolId) {
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  // Restore saved data
  useEffect(() => {
    if (savedData) {
      if (savedData.responses) setResponses(savedData.responses);
      if (savedData.currentComponent !== undefined) setCurrentComponent(savedData.currentComponent);
      if (savedData.sessionId) setSessionId(savedData.sessionId);
    }
  }, [savedData]);

  // Handle school selection
  const handleSchoolSelect = async (schoolId) => {
    try {
      const { school, error } = await schoolService.getSchool(schoolId);
      if (error) {
        console.error('Error fetching school:', error);
        setDialog({
          isOpen: true,
          title: 'Error',
          message: 'Gagal memuat data sekolah. Silakan coba lagi.',
          type: 'error'
        });
        return;
      }
      setSelectedSchool(school);
      setSelectedSchoolId(schoolId);
      setShowSchoolSelector(false);
      setLoading(true);
    } catch (err) {
      console.error('Exception in handleSchoolSelect:', err);
      setDialog({
        isOpen: true,
        title: 'Error',
        message: 'Terjadi kesalahan. Silakan coba lagi.',
        type: 'error'
      });
    }
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const { questions: groupedQuestions, rawQuestions: raw, error } = await parentAssessmentService.getQuestions();

      if (error) {
        console.error('Error loading questions:', error);
        setDialog({
          isOpen: true,
          title: 'Error',
          message: 'Gagal memuat pertanyaan. Silakan coba lagi.',
          type: 'error'
        });
        return;
      }

      setQuestions(groupedQuestions || {});
      setRawQuestions(raw || []);
    } catch (err) {
      console.error('Exception loading questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingSelect = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        rating_value: value
      }
    }));
  };

  const getCurrentComponentQuestions = () => {
    const componentName = componentOrder[currentComponent];
    return questions[componentName] || [];
  };

  const getComponentProgress = (componentName) => {
    const componentQuestions = questions[componentName] || [];
    if (componentQuestions.length === 0) return 0;
    const answered = componentQuestions.filter(q => responses[q.id]).length;
    return Math.round((answered / componentQuestions.length) * 100);
  };

  const getTotalProgress = () => {
    if (rawQuestions.length === 0) return 0;
    const answered = Object.keys(responses).length;
    return Math.round((answered / rawQuestions.length) * 100);
  };

  const handleNext = () => {
    if (currentComponent < componentOrder.length - 1) {
      setCurrentComponent(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentComponent > 0) {
      setCurrentComponent(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate all questions answered
    const unanswered = rawQuestions.filter(q => !responses[q.id]);
    if (unanswered.length > 0) {
      setDialog({
        isOpen: true,
        title: 'Belum Selesai',
        message: `Masih ada ${unanswered.length} pertanyaan yang belum dijawab. Silakan lengkapi semua pertanyaan.`,
        type: 'error'
      });
      return;
    }

    setSubmitting(true);
    try {
      // Create session if not exists
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const { session, error: sessionError } = await parentAssessmentService.createSession(
          profile?.id,
          selectedSchoolId // Target school selected by user
        );
        if (sessionError) throw sessionError;
        currentSessionId = session.id;
        setSessionId(currentSessionId);
      }

      // Submit responses
      const responsesArray = Object.values(responses);
      const { error: submitError } = await parentAssessmentService.submitResponses(
        currentSessionId,
        responsesArray,
        profile?.id
      );

      if (submitError) throw submitError;

      // Complete session
      await parentAssessmentService.completeSession(currentSessionId);

      // Calculate score
      const totalScore = responsesArray.reduce((sum, r) => sum + r.rating_value, 0);
      const maxScore = rawQuestions.length * 2;
      const percentage = Math.round((totalScore / maxScore) * 100);

      // Clear saved data
      clearSavedData();

      // Show success dialog
      setDialog({
        isOpen: true,
        title: 'Assessment Berhasil!',
        message: (
          <div className="space-y-2">
            <p>Terima kasih telah menyelesaikan penilaian.</p>
            <div className="bg-white p-4 rounded-lg mt-4">
              <p className="text-lg font-semibold">Hasil Penilaian:</p>
              <p className="text-3xl font-bold text-blue-600">{percentage}%</p>
              <p className="text-sm text-gray-600">Skor: {totalScore} / {maxScore}</p>
            </div>
          </div>
        ),
        type: 'success'
      });

      // Reset state
      setResponses({});
      setSessionId(null);
      setCurrentComponent(0);

    } catch (err) {
      console.error('Submit error:', err);
      setDialog({
        isOpen: true,
        title: 'Error',
        message: 'Gagal mengirim penilaian. Silakan coba lagi.',
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const closeDialog = () => {
    const wasSuccess = dialog.type === 'success';
    setDialog({ isOpen: false, title: '', message: '', type: 'info' });
    if (wasSuccess) {
      navigate('/');
    }
  };

  // Show school selector modal first
  if (showSchoolSelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Pilih Sekolah</h2>
            <p className="text-gray-600 mt-2">
              Pilih sekolah yang akan Anda nilai kepala sekolahnya
            </p>
          </div>

          <div className="mb-6">
            <SchoolSelector
              value={selectedSchoolId}
              onChange={handleSchoolSelect}
              label="Sekolah yang Dinilai"
              placeholder="Pilih sekolah..."
              required={true}
            />
          </div>

          <div className="text-center text-sm text-gray-500">
            Anda akan melakukan penilaian untuk kepala sekolah yang dipilih
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat pertanyaan...</p>
        </div>
      </div>
    );
  }

  const currentQuestions = getCurrentComponentQuestions();
  const currentComponentName = componentOrder[currentComponent];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* School Info Banner */}
      {selectedSchool && (
        <div className="bg-blue-600 text-white py-2 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="font-medium">{selectedSchool.nama}</span>
              <span className="text-blue-200">| NPSN: {selectedSchool.npsn}</span>
            </div>
            <button
              onClick={() => {
                if (Object.keys(responses).length > 0) {
                  setDialog({
                    isOpen: true,
                    title: 'Ganti Sekolah',
                    message: 'Mengganti sekolah akan menghapus jawaban yang sudah diisi. Lanjutkan?',
                    type: 'info',
                    onConfirm: () => {
                      setResponses({});
                      setSessionId(null);
                      setCurrentComponent(0);
                      clearSavedData();
                      setShowSchoolSelector(true);
                      setSelectedSchoolId(null);
                      setSelectedSchool(null);
                      setDialog({ isOpen: false, title: '', message: '', type: 'info' });
                    }
                  });
                } else {
                  setShowSchoolSelector(true);
                  setSelectedSchoolId(null);
                  setSelectedSchool(null);
                }
              }}
              className="text-blue-200 hover:text-white text-sm underline"
            >
              Ganti Sekolah
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Penilaian Kepala Sekolah</h1>
              <p className="text-sm text-gray-600">Oleh Orang Tua/Wali</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Progress</div>
              <div className="text-lg font-bold text-blue-600">{getTotalProgress()}%</div>
              {isSaving && <span className="text-xs text-gray-500">Menyimpan...</span>}
              {lastSaved && !isSaving && (
                <span className="text-xs text-gray-500">Tersimpan</span>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getTotalProgress()}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Component tabs */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto py-2">
            {componentOrder.map((name, idx) => (
              <button
                key={name}
                onClick={() => setCurrentComponent(idx)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  idx === currentComponent
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="mr-2">{idx + 1}.</span>
                {name}
                <span className="ml-2 text-xs opacity-75">({getComponentProgress(name)}%)</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Component header */}
          <div className="bg-blue-600 px-6 py-4">
            <h2 className="text-lg font-semibold text-white">
              {currentComponent + 1}. {currentComponentName}
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {currentQuestions.length} pertanyaan
            </p>
          </div>

          {/* Questions */}
          <div className="p-6 space-y-6">
            {currentQuestions.map((question, qIdx) => (
              <div key={question.id} className="border-b pb-6 last:border-b-0">
                <div className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium text-sm mr-4">
                    {qIdx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium mb-3">
                      {question.statement_text}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {ratingOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleRatingSelect(question.id, option.value)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all ${
                            responses[question.id]?.rating_value === option.value
                              ? option.color + ' ring-2 ring-offset-1 ring-blue-400'
                              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className="font-medium">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentComponent === 0}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentComponent === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ← Sebelumnya
            </button>

            {currentComponent === componentOrder.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Mengirim...' : 'Kirim Penilaian'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Selanjutnya →
              </button>
            )}
          </div>
        </div>

        {/* Info card */}
        <div className="mt-6 bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Panduan Pengisian:</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p><span className="font-medium text-red-600">Tidak</span> - Pernyataan tidak sesuai dengan kenyataan</p>
            <p><span className="font-medium text-yellow-600">Sebagian</span> - Pernyataan sebagian sesuai dengan kenyataan</p>
            <p><span className="font-medium text-green-600">Ya</span> - Pernyataan sepenuhnya sesuai dengan kenyataan</p>
          </div>
        </div>
      </div>

      {/* Dialog */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        type={dialog.type}
        onConfirm={dialog.onConfirm}
      >
        {dialog.message}
      </Dialog>
    </div>
  );
};

export default ParentAssessment;
